import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Box, Typography, Grid, useMediaQuery } from '@material-ui/core'
import { School, Language, Star, EmojiEvents } from '@material-ui/icons'

const EducationSection = ({ education, languages }) => {
  const isMobile = useMediaQuery('(max-width: 480px)')
  const isTablet = useMediaQuery('(max-width: 768px)')
  const [hoveredLanguage, setHoveredLanguage] = useState(null)

  const languageFlags = {
    'English': '🇺🇸',
    'Cantonese': '🇭🇰',
    'Mandarin': '🇨🇳'
  }

  return (
    <Box>
      {/* Cyberpunk Grid Background */}
      <div style={{
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        background: `
          linear-gradient(90deg, rgba(0,255,255,0.03) 1px, transparent 1px),
          linear-gradient(rgba(255,0,255,0.03) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
        opacity: 0.6
      }}/>

      <Grid container spacing={isMobile ? 2 : 4}>
        {/* Education Section */}
        <Grid item xs={12} md={8}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Box style={{
              background: `
                linear-gradient(135deg, rgba(0, 10, 20, 0.95) 0%, rgba(10, 0, 20, 0.95) 50%, rgba(0, 20, 30, 0.95) 100%),
                linear-gradient(45deg, transparent 30%, rgba(0,255,255,0.02) 31%, rgba(0,255,255,0.02) 33%, transparent 34%)
              `,
              border: '2px solid',
              borderImage: 'linear-gradient(90deg, #00ffff, #ff00ff, #00ffff) 1',
              borderRadius: '16px',
              padding: isMobile ? '1.5rem' : '2.5rem',
              backdropFilter: 'blur(15px)',
              position: 'relative',
              overflow: 'hidden'
            }}>

              {/* Scanning Line Effect */}
              <div style={{
                position: 'absolute',
                top: '0',
                left: '-100%',
                width: '100%',
                height: '2px',
                background: 'linear-gradient(90deg, transparent, #00ffff, transparent)',
                animation: 'scan 3s infinite linear'
              }}/>

              {/* Header with Institution */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '0.75rem' : '1.5rem',
                  marginBottom: isMobile ? '1.5rem' : '2.5rem',
                  position: 'relative'
                }}
              >
                <Box style={{
                  background: 'linear-gradient(135deg, #00ffff20, #00ffff05)',
                  border: '2px solid #00ffff',
                  borderRadius: '12px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <School style={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    color: '#00ffff',
                    filter: 'drop-shadow(0 0 10px #00ffff)'
                  }}/>
                </Box>

                <Box style={{ flex: 1 }}>
                  <Typography style={{
                    fontFamily: '"Orbitron", monospace',
                    fontSize: isMobile ? '1.1rem' : '1.6rem',
                    color: '#00ffff',
                    fontWeight: 'bold',
                    textShadow: '0 0 20px rgba(0, 255, 255, 0.8)',
                    lineHeight: 1.2,
                    marginBottom: '0.5rem'
                  }}>
                    Syracuse University
                  </Typography>
                  <Typography style={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: isMobile ? '0.8rem' : '1rem',
                    color: '#888888',
                    letterSpacing: '0.05em'
                  }}>
                    School of Information Studies, Syracuse, NY
                  </Typography>
                </Box>
              </motion.div>

              {/* Degree Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                style={{ marginBottom: isMobile ? '1.5rem' : '2rem' }}
              >
                <Typography style={{
                  fontFamily: '"Share Tech Mono", monospace',
                  fontSize: isMobile ? '0.9rem' : '1.2rem',
                  color: '#ff00ff',
                  textShadow: '0 0 15px rgba(255, 0, 255, 0.6)',
                  fontWeight: 'bold',
                  marginBottom: '0.75rem',
                  lineHeight: 1.4
                }}>
                  Bachelor of Science Dual Major: Information Technology/Finance (Graduated May 2013)
                </Typography>
              </motion.div>

              {/* GPA Showcase */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
                style={{ marginBottom: isMobile ? '1.5rem' : '2rem' }}
              >
                <Box style={{
                  background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.15) 0%, rgba(0, 255, 255, 0.05) 100%)',
                  border: '2px solid rgba(0, 255, 255, 0.5)',
                  borderRadius: '12px',
                  padding: isMobile ? '1.25rem' : '1.75rem',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <Typography style={{
                    fontFamily: '"Orbitron", monospace',
                    fontSize: isMobile ? '2rem' : '2.8rem',
                    color: '#00ffff',
                    fontWeight: 'bold',
                    textShadow: '0 0 30px rgba(0, 255, 255, 1)',
                    marginBottom: '0.5rem'
                  }}>
                    3.57/4.0
                  </Typography>
                  <Typography style={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: isMobile ? '0.8rem' : '1rem',
                    color: '#88ccff',
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase'
                  }}>
                    Cumulative GPA
                  </Typography>
                </Box>
              </motion.div>

              {/* Honors Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <Box style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {education.honors.map((honor, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 1 + index * 0.2 }}
                      whileHover={{ scale: 1.02, x: 5 }}
                      style={{
                        background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.1) 0%, rgba(255, 0, 255, 0.05) 100%)',
                        border: '2px solid rgba(255, 0, 255, 0.3)',
                        borderRadius: '10px',
                        padding: isMobile ? '1rem' : '1.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#ff00ff'
                        e.currentTarget.style.boxShadow = '0 0 25px rgba(255, 0, 255, 0.4)'
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 0, 255, 0.2) 0%, rgba(255, 0, 255, 0.1) 100%)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.3)'
                        e.currentTarget.style.boxShadow = 'none'
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 0, 255, 0.1) 0%, rgba(255, 0, 255, 0.05) 100%)'
                      }}
                    >
                      <EmojiEvents style={{
                        color: '#ff00ff',
                        fontSize: isMobile ? '1.5rem' : '2rem',
                        filter: 'drop-shadow(0 0 10px #ff00ff)'
                      }}/>
                      <Typography style={{
                        fontFamily: '"Share Tech Mono", monospace',
                        fontSize: isMobile ? '0.85rem' : '1rem',
                        color: '#ff00ff',
                        fontWeight: 'bold',
                        textShadow: '0 0 10px rgba(255, 0, 255, 0.5)'
                      }}>
                        {honor}
                      </Typography>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </Box>
          </motion.div>
        </Grid>

        {/* Languages Section */}
        <Grid item xs={12} md={4}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Box style={{
              background: `
                linear-gradient(135deg, rgba(20, 0, 30, 0.95) 0%, rgba(0, 20, 30, 0.95) 100%),
                linear-gradient(-45deg, transparent 30%, rgba(255,0,255,0.02) 31%, rgba(255,0,255,0.02) 33%, transparent 34%)
              `,
              border: '2px solid',
              borderImage: 'linear-gradient(90deg, #ff00ff, #00ffff, #ff00ff) 1',
              borderRadius: '16px',
              padding: isMobile ? '1.5rem' : '2rem',
              backdropFilter: 'blur(15px)',
              height: '100%',
              position: 'relative',
              overflow: 'hidden'
            }}>

              {/* Header */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: isMobile ? '1.5rem' : '2rem'
                }}
              >
                <Box style={{
                  background: 'linear-gradient(135deg, #ff00ff20, #ff00ff05)',
                  border: '2px solid #ff00ff',
                  borderRadius: '12px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Language style={{
                    fontSize: isMobile ? '1.8rem' : '2rem',
                    color: '#ff00ff',
                    filter: 'drop-shadow(0 0 10px #ff00ff)'
                  }}/>
                </Box>
                <Typography style={{
                  fontFamily: '"Orbitron", monospace',
                  fontSize: isMobile ? '1.2rem' : '1.5rem',
                  color: '#00ffff',
                  fontWeight: 'bold',
                  textShadow: '0 0 15px rgba(255, 0, 255, 0.8)'
                }}>
                  Languages
                </Typography>
              </motion.div>

              {/* Language Cards */}
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {languages.map((language, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.15 }}
                    whileHover={{ scale: 1.05, rotateY: 2 }}
                    style={{
                      background: hoveredLanguage === index
                        ? 'linear-gradient(135deg, rgba(255, 0, 255, 0.25) 0%, rgba(255, 0, 255, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(255, 0, 255, 0.1) 0%, rgba(255, 0, 255, 0.05) 100%)',
                      border: `2px solid ${hoveredLanguage === index ? '#ff00ff' : 'rgba(255, 0, 255, 0.3)'}`,
                      borderRadius: '12px',
                      padding: isMobile ? '1rem' : '1.25rem',
                      cursor: 'pointer',
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      boxShadow: hoveredLanguage === index ? '0 0 30px rgba(255, 0, 255, 0.4)' : 'none',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={() => setHoveredLanguage(index)}
                    onMouseLeave={() => setHoveredLanguage(null)}
                  >
                    <Box style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Typography style={{
                        fontFamily: '"Share Tech Mono", monospace',
                        fontSize: isMobile ? '1rem' : '1.1rem',
                        color: '#ffffff',
                        fontWeight: 'bold',
                        textShadow: '0 0 10px rgba(255, 0, 255, 0.6)'
                      }}>
                        {language}
                      </Typography>

                      <span style={{
                        fontSize: isMobile ? '1.5rem' : '2rem',
                        filter: 'drop-shadow(0 0 8px rgba(255, 0, 255, 0.5))',
                        transform: hoveredLanguage === index ? 'scale(1.2)' : 'scale(1)',
                        transition: 'transform 0.3s ease'
                      }}>
                        {languageFlags[language]}
                      </span>
                    </Box>

                    {/* Proficiency indicator */}
                    <Box style={{
                      marginTop: '0.5rem',
                      display: 'flex',
                      gap: '0.25rem'
                    }}>
                      {[...Array(language === 'English' ? 5 : 3)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.2, delay: 0.8 + index * 0.15 + i * 0.05 }}
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#ff00ff',
                            boxShadow: '0 0 6px #ff00ff'
                          }}
                        />
                      ))}
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </Box>
          </motion.div>
        </Grid>
      </Grid>

      {/* Global scan line animation styles */}
      <style>
        {`
          @keyframes scan {
            0% { left: -100%; }
            50% { left: 100%; }
            100% { left: 100%; }
          }
        `}
      </style>
    </Box>
  )
}

export default EducationSection
